import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { getExerciseAnimationUrl, getExerciseAnimationWithFallback } from '../../utils/exerciseImages';
import { partitionExercises, isWorkoutCompleted } from '../../utils/workoutHistory';
import { DeloadToggleButton } from '../cycles/DeloadToggleButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Category color mapping (same as Templates)
const CATEGORY_COLORS = {
  'chest': { bg: 'bg-red-500/20', text: 'text-red-300', border: 'border-red-400/50', label: 'Chest' },
  'back': { bg: 'bg-blue-500/20', text: 'text-blue-300', border: 'border-blue-400/50', label: 'Back' },
  'legs': { bg: 'bg-green-500/20', text: 'text-green-300', border: 'border-green-400/50', label: 'Legs' },
  'shoulders': { bg: 'bg-orange-500/20', text: 'text-orange-300', border: 'border-orange-400/50', label: 'Shoulders' },
  'arms': { bg: 'bg-purple-500/20', text: 'text-purple-300', border: 'border-purple-400/50', label: 'Arms' },
  'core': { bg: 'bg-yellow-500/20', text: 'text-yellow-300', border: 'border-yellow-400/50', label: 'Core' },
  'cardio': { bg: 'bg-pink-500/20', text: 'text-pink-300', border: 'border-pink-400/50', label: 'Cardio' },
  'full-body': { bg: 'bg-gradient-to-r from-purple-500/20 to-blue-500/20', text: 'text-purple-200', border: 'border-purple-400/50', label: 'Full Body' },
  'stretching': { bg: 'bg-teal-500/20', text: 'text-teal-300', border: 'border-teal-400/50', label: 'Stretching' },
  'plyometrics': { bg: 'bg-amber-500/20', text: 'text-amber-300', border: 'border-amber-400/50', label: 'Plyometrics' },
  'neck': { bg: 'bg-slate-500/20', text: 'text-slate-300', border: 'border-slate-400/50', label: 'Neck' }
};

const getCategoryColor = (category) => {
  const normalized = category?.toLowerCase();
  return CATEGORY_COLORS[normalized] || { bg: 'bg-gray-500/20', text: 'text-gray-300', border: 'border-gray-400/50', label: category || 'Unknown' };
};

/**
 * WorkoutViewModal - Unified component for viewing executed workout details
 * Uses React Portal to render outside the DOM hierarchy for correct fixed positioning
 * 
 * Used in:
 * - Bookings → 👁️ (view workout)
 * - Admin → Workouts → 👁️ (view executed workout)
 * - Activity Feed → View Workout (includes PRs)
 * 
 * Shows:
 * - Client info, date, time, status
 * - Personal Records (PRs) achieved in this workout
 * - Time metrics (total, active, rest)
 * - Target sets/reps AND actual_sets performed
 * - Total volume
 */
const WorkoutViewModal = ({ booking, workout, prs: externalPrs, onClose, t }) => {
  const [exerciseLibrary, setExerciseLibrary] = useState({});
  const [loadingImages, setLoadingImages] = useState(true);
  const [detectedPrs, setDetectedPrs] = useState([]);
  const [showReplayPicker, setShowReplayPicker] = useState(false);

  // Calculate estimated 1RM using Brzycki formula (consistent with backend)
  const calculateEstimated1RM = (weight, reps) => {
    if (!weight || !reps || reps <= 0) return 0;
    if (reps === 1) return weight;
    if (reps >= 37) return weight * 1.5;
    return weight * (36 / (37 - reps));
  };

  // Get best set from actual_sets using 1RM estimation (not just weight)
  // Returns only ONE best set per exercise - the one with highest estimated 1RM
  const getBestSet = (exercise) => {
    const actualSets = exercise.actual_sets || [];
    if (actualSets.length === 0) return null;
    
    let bestSet = null;
    let bestScore = 0;
    let bestIndex = -1;
    
    actualSets.forEach((set, index) => {
      const weight = set.weight || 0;
      const reps = set.reps || 0;
      const score = calculateEstimated1RM(weight, reps);
      
      if (score > bestScore) {
        bestScore = score;
        bestSet = { ...set, estimated1RM: score };
        bestIndex = index;
      }
    });
    
    return bestSet ? { ...bestSet, index: bestIndex } : null;
  };

  // Fetch exercise library to get images
  useEffect(() => {
    const fetchExerciseLibrary = async () => {
      if (!workout?.exercises || workout.exercises.length === 0) {
        setLoadingImages(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API}/exercises/list`);
        const library = {};
        (response.data || []).forEach(ex => {
          library[ex.id] = ex;
        });
        setExerciseLibrary(library);
      } catch (error) {
        console.error('Error fetching exercise library:', error);
      } finally {
        setLoadingImages(false);
      }
    };
    
    fetchExerciseLibrary();
  }, [workout]);

  // Fetch PRs from backend (single source of truth)
  useEffect(() => {
    const fetchPRs = async () => {
      if (!workout?.id) return;
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/workouts/${workout.id}/detect-prs`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data?.prs) {
          setDetectedPrs(response.data.prs);
        }
      } catch (error) {
        console.error('[PR] Error detecting PRs:', error);
      }
    };
    fetchPRs();
  }, [workout?.id]);

  // Use backend-detected PRs (or external as fallback during transition)
  const prs = detectedPrs.length > 0 ? detectedPrs : (externalPrs || []);

  if (!workout) return null;
  
  // Get exercise info from library (for images and category)
  const getExerciseInfo = (exerciseId) => {
    return exerciseLibrary[exerciseId] || {};
  };
  
  // Get workout status info
  const getWorkoutStatus = () => {
    const isFinished = workout.is_finished === true;
    const status = workout.status || 'scheduled';
    const hasActualSets = workout.exercises?.some(ex => ex.actual_sets?.length > 0);
    
    if (status === 'completed' || isFinished) {
      return { label: 'Completed', color: 'bg-green-500/20 text-green-200' };
    } else if (status === 'in_progress' || hasActualSets) {
      return { label: 'In Progress', color: 'bg-yellow-500/20 text-yellow-200' };
    } else {
      return { label: 'Scheduled', color: 'bg-blue-500/20 text-blue-200' };
    }
  };
  
  const workoutInfo = getWorkoutStatus();
  
  // Calculate total volume from actual sets
  const calculateActualVolume = () => {
    if (!workout.exercises) return 0;
    return workout.exercises.reduce((total, ex) => {
      const actualSets = ex.actual_sets || [];
      return total + actualSets.reduce((sum, set) => sum + ((set.weight || 0) * (set.reps || 0)), 0);
    }, 0);
  };
  
  const actualVolume = calculateActualVolume();
  const displayVolume = workout.total_volume || actualVolume;
  
  // Get client name (from booking or workout)
  const clientName = booking?.client_name || workout?.client_name || 'Unknown Client';
  const workoutDate = booking?.date || workout?.date || workout?.scheduled_date;
  const startTime = booking?.start_time || workout?.start_time;
  const endTime = booking?.end_time || workout?.end_time;
  
  // Use React Portal to render modal at document.body level
  // This ensures fixed positioning works correctly regardless of parent transforms
  const modalContent = (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] overflow-y-auto"
      onClick={onClose}
    >
      {/* Centering wrapper */}
      <div className="min-h-screen flex items-start sm:items-center justify-center p-4">
        <div 
          className="glass-card max-w-3xl w-full my-4 sm:my-8 max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-4rem)] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
        {/* Header */}
        <div className="flex-shrink-0 bg-gradient-to-r from-blue-900/90 to-blue-800/90 px-6 py-4 border-b border-blue-400/20 flex justify-between items-center">
          <h3 className="text-lg font-bold text-white">📊 Workout Details</h3>
          <button 
            onClick={onClose} 
            className="text-red-400 hover:text-red-300 text-xl font-bold"
          >
            ×
          </button>
        </div>
        
        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Booking/Workout Info Header */}
          <div className="bg-blue-900/30 rounded-lg p-4 mb-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-blue-300 text-xs">Client</p>
                <p className="text-white font-semibold">{clientName}</p>
              </div>
              <div>
                <p className="text-blue-300 text-xs">Date</p>
                <p className="text-white font-semibold">{workoutDate || '-'}</p>
              </div>
              {startTime && (
                <div>
                  <p className="text-blue-300 text-xs">Booking Time</p>
                  <p className="text-white font-semibold">{startTime} - {endTime || '-'}</p>
                </div>
              )}
              <div>
                <p className="text-blue-300 text-xs">Status</p>
                <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${workoutInfo.color}`}>
                  {workoutInfo.label.toUpperCase()}
                </span>
                {/* Operational quick-action: trainer can tag/untag this
                    session as deload without entering the editor. */}
                <span className="ml-2 inline-block align-middle">
                  <DeloadToggleButton
                    workoutId={workout.id}
                    isDeload={!!workout.is_deload}
                    variant="pill"
                    testIdSuffix="view-modal"
                  />
                </span>
              </div>
            </div>
            
            {/* Workout Origin */}
            {workout.template_id && workout.template_name && (
              <div className="mt-3 pt-3 border-t border-blue-400/20">
                <p className="text-blue-300 text-xs">Template</p>
                <p className="text-white text-sm">{workout.template_name}</p>
              </div>
            )}
            {!workout.template_id && (
              <div className="mt-3 pt-3 border-t border-blue-400/20">
                <p className="text-blue-300 text-xs">Origen</p>
                <p className="text-white text-sm">Personalizado</p>
              </div>
            )}
          </div>
          
          {/* Time Metrics */}
          {(workout.total_time || workout.rest_time || workout.duration) && (
            <div className="bg-green-900/20 border border-green-500/20 rounded-lg p-4 mb-4">
              <h4 className="text-green-200 font-medium mb-3 text-sm">⏱️ Workout Time Metrics</h4>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-green-300 text-xs">Total Time</p>
                  <p className="text-white text-lg font-bold">{workout.total_time || '-'} min</p>
                </div>
                <div>
                  <p className="text-green-300 text-xs">Active Time</p>
                  <p className="text-white text-lg font-bold">
                    {workout.duration || 
                      (workout.total_time && workout.rest_time 
                        ? (workout.total_time - workout.rest_time).toFixed(1) 
                        : '-')} min
                  </p>
                </div>
                <div>
                  <p className="text-green-300 text-xs">Rest Time</p>
                  <p className="text-white text-lg font-bold">{workout.rest_time || '-'} min</p>
                </div>
              </div>
            </div>
          )}
          
          {/* Session Rating */}
          {workout.session_rating && (
            <div className="bg-yellow-900/20 border border-yellow-500/20 rounded-lg p-4 mb-4 text-center">
              <p className="text-yellow-300 text-xs mb-1">Session Rating</p>
              <p className="text-white text-2xl font-bold">{workout.session_rating}/10 ⭐</p>
            </div>
          )}

          {/* Client's written feedback */}
          {workout.session_notes && workout.session_notes.trim() && (
            <div className="bg-blue-900/20 border border-blue-500/20 rounded-lg p-4 mb-4" data-testid="view-session-notes">
              <p className="text-blue-300 text-xs mb-1">📝 Client feedback</p>
              <p className="text-white text-sm whitespace-pre-wrap break-words">{workout.session_notes}</p>
            </div>
          )}
          
          {/* Personal Records (PRs) - If any */}
          {prs && prs.length > 0 && (
            <div className="bg-gradient-to-r from-yellow-900/30 to-orange-900/30 border border-yellow-500/30 rounded-lg p-4 mb-4">
              <h4 className="text-yellow-300 font-medium mb-3 flex items-center gap-2">
                🏆 Personal Records ({prs.length})
              </h4>
              <div className="space-y-2">
                {prs.map((pr, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center justify-between bg-black/20 rounded-lg p-2"
                  >
                    <span className="text-white font-medium">{pr.exercise_name}</span>
                    <span className="text-yellow-300 font-bold">
                      {pr.weight}kg × {pr.reps}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Exercise Details — history mode shows ONLY performed exercises.
              Skipped exercises collapse into a footer summary (preserves
              coach intent without bloating the view). Supersets with at
              least one performed member render the whole group dimmed. */}
          {(() => {
            const isHistory = isWorkoutCompleted(workout);
            const { performed, skipped, hasSkipped } = isHistory
              ? partitionExercises(workout)
              : { performed: workout.exercises || [], skipped: [], hasSkipped: false };
            const headerLabel = isHistory ? 'Ejercicios realizados' : 'Exercises';
            return (
          <div>
            <h4 className="text-blue-200 font-medium mb-3 text-sm">
              🏋️ {headerLabel} ({performed.length})
            </h4>
            <div className="space-y-3">
              {performed.map((exercise, idx) => {
                const exInfo = getExerciseInfo(exercise.exercise_id);
                // Use library-first fallback to ensure local WebP files are used
                const imageUrl = getExerciseAnimationWithFallback(exercise, exerciseLibrary);
                const category = exInfo.category;
                const categoryColor = getCategoryColor(category);
                const bestSet = getBestSet(exercise);
                const actualSets = exercise.actual_sets || [];
                const exerciseVolume = actualSets.reduce((sum, set) => 
                  sum + ((set.weight || 0) * (set.reps || 0)), 0);
                const isDimmed = exercise._isDimmed === true;
                
                return (
                  <div 
                    key={idx} 
                    className={`p-3 rounded-lg border ${
                      exercise.is_superset 
                        ? 'bg-orange-900/20 border-orange-500/30' 
                        : 'bg-gray-700/30 border-gray-600/30'
                    } ${isDimmed ? 'opacity-50' : ''}`}
                    data-testid={isDimmed ? `history-exercise-dimmed-${idx}` : `history-exercise-${idx}`}
                  >
                    <div className="flex gap-3">
                      {/* Exercise GIF/Image */}
                      <div className="flex-shrink-0 w-16 h-16 bg-black/30 rounded-lg overflow-hidden">
                        {imageUrl ? (
                          <img 
                            src={imageUrl} 
                            alt={exercise.exercise_name}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-2xl">🏋️</div>
                        )}
                      </div>
                      
                      {/* Exercise Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <span className="bg-blue-600 text-white text-xs font-bold px-2 py-0.5 rounded mr-2">
                              {idx + 1}
                            </span>
                            <span className="text-white font-semibold">
                              {exercise.exercise_name_es || exercise.exercise_name}
                            </span>
                            {exercise.is_superset && (
                              <span className="ml-2 bg-orange-500/30 text-orange-300 text-xs px-2 py-0.5 rounded">
                                🔗 Superset {exercise.superset_group || 'A'}
                              </span>
                            )}
                          </div>
                          {exerciseVolume > 0 && (
                            <span className="text-blue-300 text-xs whitespace-nowrap">
                              {exerciseVolume.toLocaleString()} kg
                            </span>
                          )}
                        </div>
                        
                        {/* Sets Display with Best Set Highlighting */}
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {actualSets.length > 0 ? actualSets.map((set, setIdx) => {
                            const isBestSet = bestSet && setIdx === bestSet.index;
                            
                            return (
                              <div 
                                key={setIdx}
                                className={`px-2 py-1 rounded text-sm font-mono ${
                                  isBestSet 
                                    ? 'bg-yellow-500/30 border border-yellow-500 text-yellow-200' 
                                    : 'bg-gray-600/50 text-gray-200'
                                }`}
                              >
                                {set.reps || 0}×{set.weight || 0}kg
                                {isBestSet && <span className="ml-1">⭐</span>}
                              </div>
                            );
                          }) : (
                            <span className="text-gray-500 text-sm italic">No sets recorded</span>
                          )}
                        </div>
                        
                        {/* Best Set Info with Est. 1RM */}
                        {bestSet && (
                          <div className="mt-2 text-yellow-400 text-xs">
                            ⭐ Best Set: {bestSet.reps}×{bestSet.weight}kg 
                            <span className="text-yellow-500/70 ml-1">
                              (Est. 1RM: {bestSet.estimated1RM.toFixed(1)}kg)
                            </span>
                          </div>
                        )}
                        
                        {/* Exercise Note */}
                        {exercise.note && (
                          <p className="mt-2 text-yellow-300/80 text-xs italic">📝 {exercise.note}</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {(!workout.exercises || workout.exercises.length === 0) && (
                <p className="text-blue-300 text-center py-4">No exercises in this workout</p>
              )}

              {/* Skipped exercises footer (history only) — coach intent
                  preserved without bloating the main view. */}
              {isHistory && hasSkipped && (
                <SkippedExercisesFooter skipped={skipped} />
              )}
            </div>
          </div>
            );
          })()}
          
          {/* Booking Info and Workout ID */}
          <div className="mt-4 bg-gray-700/30 p-3 rounded-lg">
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-400">Booking:</span>
              <span className={startTime ? 'text-purple-300' : 'text-gray-500'}>
                {startTime ? `${workoutDate} ${startTime} - ${endTime || '-'}` : 'No booking linked'}
              </span>
            </div>
            {workout.id && (
              <div className="flex justify-between items-center text-sm mt-1">
                <span className="text-gray-400">Workout ID:</span>
                <span className="text-gray-500 font-mono text-xs">{workout.id}</span>
              </div>
            )}
          </div>
          
          {/* Session Notes */}
          {workout.notes && (
            <div className="mt-4 bg-purple-900/20 border border-purple-500/20 rounded-lg p-4">
              <p className="text-purple-300 text-xs mb-1">Session Notes</p>
              <p className="text-white text-sm">{workout.notes}</p>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-blue-400/20 bg-blue-900/30 flex items-center justify-between gap-3">
          {/* ↻ Replay action — only for completed sessions */}
          {isWorkoutCompleted(workout) ? (
            <button
              type="button"
              onClick={() => setShowReplayPicker(true)}
              className="px-4 py-2 rounded-lg bg-purple-500/20 border border-purple-400/40 text-purple-200 hover:bg-purple-500/30 hover:border-purple-300/60 transition text-sm font-medium"
              data-testid="replay-session-btn"
              title="Repetir esta sesión exactamente como ocurrió"
            >
              ↻ Repetir esta sesión
            </button>
          ) : <span />}
          <button onClick={onClose} className="btn-secondary">
            Close
          </button>
        </div>

        {/* Replay date/booking picker — opens above the modal */}
        {showReplayPicker && (
          <ReplayPickerModal
            sourceWorkout={workout}
            onClose={() => setShowReplayPicker(false)}
            onSuccess={(newWorkout) => {
              setShowReplayPicker(false);
              if (typeof onClose === 'function') onClose();
              // Optional: callback to parent to navigate to the new workout
              if (typeof window !== 'undefined') {
                alert(`✅ Sesión repetida creada para ${newWorkout.date}.`);
              }
            }}
          />
        )}
        </div>
      </div>
    </div>
  );
  
  // Render to document.body using React Portal
  return ReactDOM.createPortal(modalContent, document.body);
};

/**
 * Collapsed list of exercises that were planned but never performed.
 * Names only — preserves coach intent without competing with what the
 * client actually did.
 */
const SkippedExercisesFooter = ({ skipped }) => {
  const [open, setOpen] = useState(false);
  if (!skipped || skipped.length === 0) return null;
  return (
    <div
      className="mt-3 rounded-lg border border-white/5 bg-white/[0.02] text-sm"
      data-testid="history-skipped-footer"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 text-slate-400 hover:text-slate-200 transition"
      >
        <span>
          + {skipped.length} {skipped.length === 1 ? 'ejercicio planificado no realizado' : 'ejercicios planificados no realizados'}
        </span>
        <span className="text-xs">{open ? '▴' : '▾'}</span>
      </button>
      {open && (
        <ul className="px-4 pb-3 space-y-1 text-slate-500">
          {skipped.map((ex, i) => (
            <li key={i} className="flex items-baseline gap-2">
              <span className="text-slate-600">·</span>
              <span>{ex.exercise_name_es || ex.exercise_name || 'Ejercicio sin nombre'}</span>
              {ex.is_superset && ex.superset_group && (
                <span className="ml-auto text-[10px] text-orange-400/60 uppercase tracking-wider">
                  superset {ex.superset_group}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/**
 * Replay picker — small modal asking WHEN the new session should land.
 *
 * Two simple options:
 *   1) Today as a flexible workout (no booking) — for trainers who want to
 *      schedule the replay outside the booking grid.
 *   2) A specific date (text input) + optional booking_id (resolved later
 *      via the planner if the trainer wants to attach it to a slot).
 *
 * Calls POST /api/workouts/{id}/replay.
 */
const ReplayPickerModal = ({ sourceWorkout, onClose, onSuccess }) => {
  const today = new Date().toISOString().split('T')[0];
  const [date, setDate] = useState(today);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/workouts/${sourceWorkout.id}/replay`,
        { date, booking_id: null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onSuccess(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'No se pudo crear la repetición');
    } finally {
      setSubmitting(false);
    }
  };

  const performedCount = (sourceWorkout?.exercises || [])
    .filter((ex) => Array.isArray(ex.actual_sets) && ex.actual_sets.length > 0).length;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="replay-picker-overlay"
    >
      <div
        className="bg-slate-900 border border-purple-400/30 rounded-xl max-w-md w-full p-5 max-h-[90dvh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-white mb-1">↻ Repetir esta sesión</h3>
        <p className="text-sm text-slate-400 mb-4">
          Se creará una nueva sesión con los <strong className="text-purple-200">{performedCount} ejercicios realizados</strong> y la
          estructura exacta por serie (reps, peso, descanso).
        </p>

        <label className="block text-xs uppercase tracking-widest text-slate-400 mb-1">Fecha</label>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="w-full bg-slate-800 border border-white/10 rounded-md px-3 py-2 text-white mb-4"
          data-testid="replay-date-input"
        />

        <p className="text-xs text-slate-500 mb-4 italic">
          Se creará como rutina libre. Podrás asignarla a una cita desde el planificador.
        </p>

        {error && (
          <div className="mb-3 p-2 rounded bg-red-500/15 border border-red-400/30 text-red-200 text-sm">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-slate-300 hover:bg-white/[0.04] transition text-sm disabled:opacity-50"
            data-testid="replay-cancel-btn"
          >
            Cancelar
          </button>
          <button
            onClick={submit}
            disabled={submitting || !date}
            className="px-4 py-2 rounded-lg bg-purple-500/30 border border-purple-400/40 text-purple-100 hover:bg-purple-500/40 transition text-sm font-medium disabled:opacity-50"
            data-testid="replay-confirm-btn"
          >
            {submitting ? 'Creando…' : 'Crear repetición'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default WorkoutViewModal;
